import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { fetchAll } from '../lib/fetchAll'
import { Icon, Avatar, StatusPill, Spinner, EmptyState, normStatus, timeAgo, requesterName } from './ui'

const GIORNI = 14

function saluto() {
  const h = new Date().getHours()
  return h < 13 ? 'Buongiorno' : h < 18 ? 'Buon pomeriggio' : 'Buonasera'
}

export default function Dashboard({ profile, openTicket, setView }) {
  const [tickets, setTickets] = useState(null)
  const [profiles, setProfiles] = useState([])

  useEffect(() => {
    let alive = true
    fetchAll(() => supabase.from('tickets')
      .select('*, created_by_profile:profiles!tickets_created_by_fkey(name), assigned_to_profile:profiles!tickets_assigned_to_fkey(name)')
      .order('created_at', { ascending: false }).order('id'))
      .then(({ data }) => { if (alive) setTickets(data || []) })
    supabase.from('profiles').select('id, name').then(({ data }) => { if (alive) setProfiles(data || []) })
    return () => { alive = false }
  }, [])

  if (!tickets) return <div className="empty"><Spinner size={22} /></div>

  const st = (t) => normStatus(t.status)
  const oggi = new Date(); oggi.setHours(0, 0, 0, 0)
  const nonRisolti = tickets.filter(t => st(t) !== 'risolto')
  const k = {
    attivi: nonRisolti.length,
    aperti: tickets.filter(t => st(t) === 'aperto').length,
    lavorazione: tickets.filter(t => st(t) === 'in_lavorazione').length,
    nonAssegnati: nonRisolti.filter(t => !t.assigned_to).length,
    risolti: tickets.filter(t => st(t) === 'risolto').length,
    oggi: tickets.filter(t => new Date(t.created_at) >= oggi).length,
    miei: nonRisolti.filter(t => t.assigned_to === profile?.id).length,
  }

  // Ticket creati negli ultimi 14 giorni
  const giorni = Array.from({ length: GIORNI }, (_, i) => {
    const d = new Date(oggi); d.setDate(d.getDate() - (GIORNI - 1 - i))
    return d
  })
  const serie = giorni.map(d => {
    const fine = new Date(d); fine.setDate(fine.getDate() + 1)
    return { d, n: tickets.filter(t => { const c = new Date(t.created_at); return c >= d && c < fine }).length }
  })
  const maxSerie = Math.max(1, ...serie.map(s => s.n))
  const totSerie = serie.reduce((a, s) => a + s.n, 0)

  // Carico di lavoro: ticket non risolti per operatore
  const carico = [
    ...profiles.map(p => ({ id: p.id, name: p.name, n: nonRisolti.filter(t => t.assigned_to === p.id).length })),
    { id: null, name: 'Non assegnati', n: k.nonAssegnati },
  ].sort((a, b) => b.n - a.n)
  const maxCarico = Math.max(1, ...carico.map(c => c.n))

  const recenti = tickets.slice(0, 8)

  const KPI = [
    { key: 'attivi', label: 'Da gestire', value: k.attivi, icon: 'inbox', tint: '#eef2ff', color: '#4f46e5', foot: `${k.miei} assegnati a te`, go: 'tickets' },
    { key: 'nonAssegnati', label: 'Non assegnati', value: k.nonAssegnati, icon: 'userX', tint: '#fef3f2', color: '#d92d20', foot: k.nonAssegnati ? 'Da prendere in carico' : 'Tutto assegnato', go: 'unassigned' },
    { key: 'aperti', label: 'Aperti', value: k.aperti, icon: 'circleDot', tint: '#fffaeb', color: '#dc6803', foot: 'In attesa di lavorazione', go: 'open' },
    { key: 'lavorazione', label: 'In lavorazione', value: k.lavorazione, icon: 'clock', tint: '#fef3f2', color: '#f04438', foot: 'Su cui si sta lavorando', go: 'progress' },
    { key: 'risolti', label: 'Risolti', value: k.risolti, icon: 'checkCircle', tint: '#ecfdf3', color: '#079455', foot: `${tickets.length} ticket in totale`, go: 'resolved' },
  ]

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{saluto()}, {profile?.name?.split(' ')[0]}</h1>
          <p className="page-sub">
            {new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
            {' · '}{k.oggi === 0 ? 'nessun nuovo ticket oggi' : k.oggi === 1 ? '1 nuovo ticket oggi' : `${k.oggi} nuovi ticket oggi`}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setView('new')}><Icon name="plus" size={16} strokeWidth={2.5} /> Nuovo ticket</button>
      </div>

      <div className="kpis">
        {KPI.map(x => (
          <button key={x.key} className="kpi" onClick={() => setView(x.go)}>
            <div className="kpi-top">
              <span className="kpi-label">{x.label}</span>
              <span className="kpi-icon" style={{ background: x.tint, color: x.color }}><Icon name={x.icon} size={16} /></span>
            </div>
            <div className="kpi-value">{x.value}</div>
            <div className="kpi-foot">{x.foot}</div>
          </button>
        ))}
      </div>

      <div className="dash-grid">
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Nuovi ticket</div>
              <div className="card-sub">Ultimi {GIORNI} giorni · {totSerie} in totale</div>
            </div>
            <span className="kpi-icon" style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}><Icon name="barChart" size={16} /></span>
          </div>
          <div className="card-body">
            <div className="chart">
              {serie.map(({ d, n }, i) => (
                <div key={i} className="chart-col" title={`${d.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' })}: ${n} ticket`}>
                  {n > 0 && <span className="chart-val">{n}</span>}
                  <div className={`chart-bar${n === 0 ? ' zero' : ''}`} style={{ height: `${Math.max(3, (n / maxSerie) * 120)}px` }} />
                  <span className="chart-lbl">{i === GIORNI - 1 ? 'oggi' : d.toLocaleDateString('it-IT', { day: 'numeric', month: 'numeric' })}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Carico per operatore</div>
              <div className="card-sub">Ticket non risolti assegnati</div>
            </div>
          </div>
          <div className="card-body" style={{ paddingTop: 8, paddingBottom: 8 }}>
            {carico.map(c => (
              <div key={c.id || 'none'} className="load-row">
                {c.id ? <Avatar name={c.name} size={26} /> : <span className="avatar" style={{ width: 26, height: 26, background: 'var(--surface-3)', color: 'var(--text-3)' }}><Icon name="userX" size={13} /></span>}
                <div className="minw0">
                  <div className="truncate" style={{ fontSize: 13, fontWeight: 500, color: c.id ? 'var(--text)' : 'var(--text-3)' }}>{c.name}</div>
                  <div className="load-bar"><div className="load-fill" style={{ width: `${(c.n / maxCarico) * 100}%`, background: c.id ? 'var(--accent)' : '#f97066' }} /></div>
                </div>
                <span className="tnum" style={{ textAlign: 'right', fontWeight: 600, fontSize: 13 }}>{c.n}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Attività recente</div>
          <button className="btn btn-ghost btn-sm" onClick={() => setView('tickets')}>Vedi tutti <Icon name="arrowRight" size={14} /></button>
        </div>
        {recenti.length === 0 ? (
          <EmptyState title="Nessun ticket ancora" text="Le richieste dei clienti compariranno qui."
            action={<button className="btn btn-primary" onClick={() => setView('new')}><Icon name="plus" size={16} /> Crea il primo ticket</button>} />
        ) : recenti.map(t => (
          <div key={t.id} className="list-row" onClick={() => openTicket(t)}>
            <Avatar name={t.recipient_email || t.created_by_profile?.name || '?'} size={32} />
            <div className="minw0 grow">
              <div className="subj">
                {t.number != null && <span className="subj-num">#{t.number}</span>}
                <span className="subj-title truncate">{t.title}</span>
              </div>
              <div className="subj-snip truncate">
                {t.recipient_email ? requesterName(t.recipient_email) : (t.created_by_profile?.name || '—')}
                {' · '}{t.assigned_to_profile ? `assegnato a ${t.assigned_to_profile.name}` : 'non assegnato'}
              </div>
            </div>
            <StatusPill status={t.status} size="sm" />
            <span className="c-time" style={{ width: 84, textAlign: 'right' }}>{timeAgo(t.created_at)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
